import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Form,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col
} from "reactstrap";
import Validate from "validate.js";

import ColorInput from "./ColorInput";
import LanguageSelect from "./LanguageSelect";
import BibleSelect from "./BibleSelect";
import siteConstraints from "../constraints/siteConstraints";
import InvalidFeedback from "./InvalidFeedback";
import ImageUploader from "./ImageUploader";
import TextEditor from "./TextEditor";

class SiteForm extends Component {
  constructor(props) {
    super(props);
    const site = this.props.site || {};
    const defaultSite = {
      subdomain: "",
      title: "",
      enabledUserAccounts: false,
      bibles: [],
      includeAllBibles: false,
      showBibleAbbreviations: false,
      scriptDirection: "LTR",
      bibleFont: "Noto Serif",
      uiFont: "Noto Sans",
      customDomain: "",
      description: "",
      googleAnalyticsId: "",
      primaryColor: "#0693e3",
      installedName: "",
      appStoreName: "",
      appStoreSubtitle: "",
      storeDescription: "",
      keywords: "",
      isWhiteHeader: false,
      published: true,
      primaryLanguage: null,
      secondaryLanguage: null
    };

    this.state = {
      site: {
        ...defaultSite,
        ...site
      },
      notifications: [],
      validationErrors: {}
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  isLessThan7DaysOld() {
    const { updatedAt } = this.state.site;
    if (!updatedAt) return false;

    const sevenDaysLater = new Date(updatedAt);
    sevenDaysLater.setDate(sevenDaysLater.getDate() + 7);
    return Date.now() < sevenDaysLater;
  }

  handleChange(event) {
    const { name, value, checked } = event.target;

    const formValue = event.target.type === "checkbox" ? checked : value;

    if (name in siteConstraints) {
      const validationErrors = Validate(
        { [name]: formValue },
        { [name]: siteConstraints[name] }
      );
      if (validationErrors) {
        this.setState({
          validationErrors: {
            [name]: validationErrors[name]
          }
        });
      } else {
        this.setState({
          validationErrors: {
            name: undefined
          }
        });
      }
    }
    if (name)
      this.setState(state => ({
        site: {
          ...state.site,
          [name]: formValue
        }
      }));
  }

  handleSubmit() {
    window.scrollTo(0, 0);
    this.setState(state => {
      const validationErrors = Validate(state.site, siteConstraints) || {};
      if (Object.keys(validationErrors).length === 0) {
        this.props.onSubmit(state.site);
      }
      return { validationErrors };
    });
  }

  render() {
    const { site, validationErrors } = this.state;
    const isValid = name => (name in validationErrors ? false : undefined);
    const isLessThan7DaysOld = this.isLessThan7DaysOld();

    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-6">
          {this.state.notifications.map(notification => (
            <Alert
              key={notification.message}
              color={notification.type === "error" ? "danger" : undefined}
            >
              {notification.message}
            </Alert>
          ))}
          <Form>
            <Row>
              <Col>
                <FormGroup>
                  <Label for="title">Site Title</Label>
                  <Input
                    name="title"
                    type="text"
                    onChange={this.handleChange}
                    placeholder="My Bible Site"
                    value={site.title}
                    valid={isValid("title")}
                  />
                  <InvalidFeedback
                    name="title"
                    validationErrors={validationErrors}
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label for="subdomain">Sub-domain</Label>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      https://
                    </InputGroupAddon>
                    <Input
                      name="subdomain"
                      type="text"
                      onChange={this.handleChange}
                      placeholder="my-site-id"
                      value={site.subdomain}
                      valid={isValid("subdomain")}
                    />
                    <InputGroupAddon addonType="append">
                      .global.bible
                    </InputGroupAddon>
                  </InputGroup>
                  <InvalidFeedback
                    name="subdomain"
                    validationErrors={validationErrors}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <FormGroup>
                  <input
                    type="checkbox"
                    name="enabledUserAccounts"
                    onChange={this.handleChange}
                    checked={site.enabledUserAccounts}
                    valid={isValid("enabledUserAccounts")}
                  />{" "}
                  <Label for="enabledUserAccounts">Enable user accounts</Label>
                  <p>
                    <small>
                      <i>
                        Allows user&apos;s to sign up, sign in, and use account
                        based features.
                      </i>
                    </small>
                  </p>
                </FormGroup>
              </Col>
            </Row>
            <h3>Theme</h3>
            <Row>
              <Col>
                <FormGroup>
                  <Label for="primaryColor">Primary</Label>
                  <ColorInput
                    label="Primary Color"
                    value={site.primaryColor}
                    name="primaryColor"
                    onChange={this.handleChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <FormGroup>
                  <input
                    type="checkbox"
                    name="isWhiteHeader"
                    onChange={this.handleChange}
                    checked={
                      site.isWhiteHeader === undefined || site.isWhiteHeader
                    }
                    valid={isValid("isWhiteHeader")}
                  />{" "}
                  <Label for="isWhiteHeader">
                    Set header background to white
                  </Label>
                </FormGroup>
              </Col>
            </Row>
            <h3>Images</h3>
            <Row>
              <Col>
                <Label>Logo</Label>
                <ImageUploader
                  name="logoImage"
                  onChange={this.handleChange}
                  filename={this.state.site.logoImage}
                  background={this.state.site.primaryColor}
                />
              </Col>
              <Col className="mb-4">
                <Label>Icon</Label>
                <ImageUploader
                  name="iconImage"
                  onChange={this.handleChange}
                  filename={this.state.site.iconImage}
                  background={this.state.site.primaryColor}
                />
              </Col>
            </Row>
            <h3>UI Languages</h3>
            <Row>
              <Col>
                <FormGroup>
                  <Label>Primary Language</Label>
                  <LanguageSelect
                    name="primaryLanguage"
                    value={site.primaryLanguage}
                    onChange={this.handleChange}
                  />
                  <InvalidFeedback
                    name="primaryLanguage"
                    validationErrors={validationErrors}
                  />
                  <p>
                    <small>
                      <i>* Marks an language that is partially complete.</i>
                    </small>
                  </p>
                </FormGroup>
                <FormGroup>
                  <Label>Secondary Language</Label>
                  <LanguageSelect
                    name="secondaryLanguage"
                    value={site.secondaryLanguage}
                    onChange={this.handleChange}
                    completedOnly
                  />
                  <InvalidFeedback
                    name="secondaryLanguage"
                    validationErrors={validationErrors}
                  />
                  <p>
                    <small>
                      <i>
                        A secondary language must be a language with complete
                        translations.
                      </i>
                    </small>
                  </p>
                </FormGroup>
              </Col>
            </Row>
            <h3>Bibles</h3>
            <Row>
              <Col>
                <FormGroup>
                  <Label>Add a Bible</Label>
                  <BibleSelect
                    name="bibles"
                    value={site.bibles}
                    onChange={this.handleChange}
                  />
                  <InvalidFeedback
                    name="bibles"
                    validationErrors={validationErrors}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col className="mb-3">
                <p>Bible Settings</p>
                <FormGroup>
                  <input
                    type="checkbox"
                    name="includeAllBibles"
                    onChange={this.handleChange}
                    checked={site.includeAllBibles}
                    valid={isValid("includeAllBibles")}
                  />{" "}
                  <Label for="includeAllBibles">Include all bibles</Label>
                  <p>
                    {" "}
                    <small>
                      <i>
                        Makes all texts in all languages available on a site.
                      </i>
                    </small>
                  </p>
                </FormGroup>
                <FormGroup>
                  <input
                    type="checkbox"
                    name="showBibleAbbreviations"
                    onChange={this.handleChange}
                    checked={
                      site.showBibleAbbreviations === undefined ||
                      site.showBibleAbbreviations
                    }
                    valid={isValid("showBibleAbbreviations")}
                  />{" "}
                  <Label for="showBibleAbbreviations">
                    Show Bible Abbreviations
                  </Label>
                  <p>
                    {" "}
                    <small>
                      <i>
                        Showing bible abbreviations will allow users to see them
                        within the UI.
                      </i>
                    </small>
                  </p>
                </FormGroup>
              </Col>
            </Row>
            <h3>Fonts</h3>
            <Row>
              <Col>
                <FormGroup>
                  <Label>Script Direction</Label>
                  <Input
                    type="select"
                    name="scriptDirection"
                    onChange={this.handleChange}
                    value={site.scriptDirection}
                  >
                    <option value="LTR">Left-to-Right</option>
                    <option value="RTL">Right-to-Left</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label>Bible Text Font</Label>
                  <Input
                    type="select"
                    name="bibleFont"
                    onChange={this.handleChange}
                    value={site.bibleFont}
                  >
                    <option value="Charis SIL">Charis SIL</option>
                    <option value="Noto Sans">Noto Sans</option>
                  </Input>
                </FormGroup>
              </Col>

              <Col>
                <FormGroup>
                  <Label>UI Font</Label>
                  <Input
                    type="select"
                    name="uiFont"
                    onChange={this.handleChange}
                    value={site.uiFont}
                  >
                    <option value="Charis SIL">Charis SIL</option>
                    <option value="Noto Sans">Noto Sans</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Custom Domain</Label>
              <InputGroup>
                <InputGroupAddon addonType="prepend">https://</InputGroupAddon>
                <Input
                  name="customDomain"
                  type="text"
                  onChange={this.handleChange}
                  value={site.customDomain}
                  placeholder="mybiblesite.com"
                  valid={isValid("customDomain")}
                />
              </InputGroup>
              <InvalidFeedback
                name="customDomain"
                validationErrors={validationErrors}
              />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <TextEditor
                name="description"
                initialValue={site.description}
                onChange={this.handleChange}
              />
              {/* <Input
              name="description"
              type="textarea"
              onChange={this.handleChange}
              value={site.description}
              placeholder=""
            /> */}
            </FormGroup>
            <FormGroup>
              <Label>Google Analytics ID</Label>
              <Input
                name="googleAnalyticsId"
                type="text"
                value={site.googleAnalyticsId}
                onChange={this.handleChange}
                placeholder="UA-000000-0"
              />
            </FormGroup>
            <FormGroup>
              <Label>API.Bible Key</Label>
              <Input
                name="apiBibleKey"
                type="text"
                onChange={this.handleChange}
                placeholder="xxxxxxx-xxxxxxxxx-xxxxxxx"
              />
            </FormGroup>
            <Row>
              <Col>
                <FormGroup>
                  <Label for="linkFacebook">Facebook</Label>
                  <Input
                    type="text"
                    name="linkFacebook"
                    onChange={this.handleChange}
                    value={site.linkFacebook}
                    valid={isValid("linkFacebook")}
                  />
                  <InvalidFeedback
                    name="linkFacebook"
                    validationErrors={validationErrors}
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label for="linkTwitter">Twitter</Label>
                  <Input
                    type="text"
                    name="linkTwitter"
                    onChange={this.handleChange}
                    value={site.linkTwitter}
                    valid={isValid("linkTwitter")}
                  />
                  <InvalidFeedback
                    name="linkTwitter"
                    validationErrors={validationErrors}
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label for="linkWebsite">Website</Label>
                  <Input
                    type="text"
                    name="linkWebsite"
                    onChange={this.handleChange}
                    value={site.linkWebsite}
                    valid={isValid("linkWebsite")}
                  />
                  <InvalidFeedback
                    name="linkWebsite"
                    validationErrors={validationErrors}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <FormGroup>
                  <input
                    type="checkbox"
                    name="published"
                    onChange={this.handleChange}
                    checked={site.published === undefined || site.published}
                    valid={isValid("published")}
                  />{" "}
                  <Label for="published">Publish Site</Label>
                  <p>
                    {" "}
                    <small>
                      <i>
                        Published sites will appear in search results on
                        Global.Bible
                      </i>
                    </small>
                  </p>
                </FormGroup>
              </Col>
            </Row>
            <h4>App Stores</h4>
            <FormGroup>
              <Label for="installedName">Installed App Name</Label>
              <Input
                type="text"
                name="installedName"
                id="installedName"
                value={site.installedName}
                onChange={this.handleChange}
                valid={isValid("installedName")}
              />
              <InvalidFeedback
                name="installedName"
                validationErrors={validationErrors}
              />
            </FormGroup>
            <FormGroup>
              <Label for="appStoreName">App Store Name / Title</Label>
              <Input
                type="text"
                name="appStoreName"
                id="appStoreName"
                placeholder=""
                value={site.appStoreName}
                onChange={this.handleChange}
                valid={isValid("appStoreName")}
              />
              <InvalidFeedback
                name="appStoreName"
                validationErrors={validationErrors}
              />
            </FormGroup>
            <FormGroup>
              <Label for="appStoreSubtitle">App Store Subtitle</Label>
              <Input
                type="text"
                name="appStoreSubtitle"
                id="appStoreSubtitle"
                placeholder=""
                value={site.appStoreSubtitle}
                onChange={this.handleChange}
                valid={isValid("appStoreSubtitle")}
              />
              <InvalidFeedback
                name="appStoreSubtitle"
                validationErrors={validationErrors}
              />
            </FormGroup>

            <FormGroup>
              <Label for="storeDescription">App Store Description</Label>
              <Input
                type="textarea"
                name="storeDescription"
                id="storeDescription"
                placeholder=""
                value={site.storeDescription}
                onChange={this.handleChange}
                valid={isValid("storeDescription")}
              />
              <InvalidFeedback
                name="storeDescription"
                validationErrors={validationErrors}
              />
            </FormGroup>

            <FormGroup>
              <Label for="keywords">Keywords</Label>
              {/* <KeywordInput
              name="keywords"
              id="keywords"
              value={this.state.site.keywords || ""}
              onChange={this.handleChange}
            /> */}
              <Input
                type="text"
                name="keywords"
                id="keywords"
                placeholder="comma separated"
                value={site.keywords}
                onChange={this.handleChange}
                valid={isValid("keywords")}
              />
              <InvalidFeedback
                name="keywords"
                validationErrors={validationErrors}
              />
            </FormGroup>
            <h5>Published App Store Links</h5>

            <Row>
              <Col>
                <FormGroup>
                  <Label for="linkAppStore">iOS App Store</Label>
                  <Input
                    type="text"
                    name="linkAppStore"
                    onChange={this.handleChange}
                    value={site.linkAppStore}
                    valid={isValid("linkAppStore")}
                  />
                  <InvalidFeedback
                    name="linkAppStore"
                    validationErrors={validationErrors}
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label for="linkPlayStore">Google Play Store</Label>
                  <Input
                    type="text"
                    name="linkPlayStore"
                    onChange={this.handleChange}
                    value={site.linkPlayStore}
                    valid={isValid("linkPlayStore")}
                  />
                  <InvalidFeedback
                    name="linkPlayStore"
                    validationErrors={validationErrors}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col>
                {site.ipaLocation && isLessThan7DaysOld && (
                  <div>
                    <span>iOS:</span>
                    <a href={site.ipaLocation}>Download ipa</a>
                  </div>
                )}
              </Col>
              <Col>
                {site.apkLocation && isLessThan7DaysOld && (
                  <div>
                    <span>Android:</span>
                    <a href={site.apkLocation}>Download apk</a>
                  </div>
                )}
              </Col>
            </Row>
            <Row>
              <Col>
                <FormGroup>
                  <Button onClick={this.handleSubmit}>Submit</Button>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    );
  }
}

SiteForm.defaultProps = {
  site: {}
};

SiteForm.propTypes = {
  site: PropTypes.shape({
    title: PropTypes.string
  }),
  onSubmit: PropTypes.func.isRequired
};

export default SiteForm;
